/* eslint-disable */
import React, { useState } from 'react';
import { Box, Typography, Checkbox, Popover } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

/**
 * DropdownSelect – shared dropdown component.
 *
 * Props:
 *   options     {Array<{value, label}>}  List of options.
 *   value       {string|string[]}        Selected value(s). Array when multiple=true.
 *   onChange    {Function}               Called with new value/array on change.
 *   placeholder {string}                 Placeholder text shown when nothing selected.
 *   multiple    {boolean}                Enable multi-select mode (default false).
 *   width       {string}                 CSS width of the trigger (default '100%').
 *   height      {string}                 CSS height of the trigger (default '45px').
 */
const DropdownSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Sélectionner',
  multiple = false,
  width = '100%',
  height = '45px',
  fontSize = '13px',
  disabled = false,
}) => {
  const [anchor, setAnchor] = useState(null);

  const isOpen = Boolean(anchor);

  // Normalise value to array for internal logic
  const selected = multiple
    ? Array.isArray(value) ? value : []
    : value !== undefined && value !== null ? value : null;

  const hasValue = multiple ? selected.length > 0 : selected !== null && selected !== '';

  // Build trigger label
  const triggerLabel = (() => {
    if (!hasValue) return placeholder;
    if (!multiple) {
      const opt = options.find((o) => o.value === selected);
      return opt ? opt.label : placeholder;
    }
    if (selected.length > 2) return `${selected.length} sélectionnés`;
    return selected
      .map((v) => options.find((o) => o.value === v)?.label ?? v)
      .join(', ');
  })();

  const handleOpen = (e) => { if (!disabled) setAnchor(e.currentTarget); };
  const handleClose = () => setAnchor(null);

  const handleOptionClick = (optValue) => {
    if (!multiple) {
      onChange && onChange(optValue);
      handleClose();
    } else {
      const current = Array.isArray(value) ? value : [];
      const next = current.includes(optValue)
        ? current.filter((v) => v !== optValue)
        : [...current, optValue];
      onChange && onChange(next);
    }
  };

  return (
    <Box sx={{ width, flexShrink: 0, position: 'relative' }}>
      {/* Trigger */}
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: isOpen && hasValue ? '1px solid #007BC6' : hasValue ? '1px solid #007BC6' : '1px solid #DEE5EE',
          borderRadius: '8px',
          px: '12px',
          height,
          backgroundColor: disabled ? '#F1F5F9' : '#F8FAFC',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Box sx={{ overflow: 'hidden' }}>
          {hasValue && (
            <Typography sx={{ fontSize: '12px', color: '#007BC6', fontWeight: 600, lineHeight: 1, fontFamily: "'Open Sans'" }}>
              {placeholder}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize,
              lineHeight: '22px',
              fontWeight: hasValue ? 600 : 600,
              color: hasValue ? '#385576' : '#8397AE',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontFamily: "'Open Sans', sans-serif",
            }}
          >
            {triggerLabel}
          </Typography>
        </Box>
        {isOpen
          ? <KeyboardArrowUpIcon sx={{ fontSize: 28, color: '#A0AEC0', flexShrink: 0 }} />
          : <KeyboardArrowDownIcon sx={{ fontSize: 28, color: '#A0AEC0', flexShrink: 0 }} />}
      </Box>

      {/* Popover */}
      <Popover
        open={isOpen}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ mt: '6px' }}
        PaperProps={{
          sx: {
            width: anchor ? anchor.offsetWidth : 'auto',
            border: '1px solid #F7F7F7',
            borderRadius: '12px',
            boxShadow: '0px 4px 16px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: '8px', minWidth: '160px', maxHeight: '300px', overflowY: 'auto' }}>
          {options.map((opt, i) => {
            const isSelected = multiple
              ? (Array.isArray(value) ? value : []).includes(opt.value)
              : value === opt.value;

            return (
              <Box
                key={opt.value}
                onClick={() => handleOptionClick(opt.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  px: '8px',
                  py: '10px',
                  cursor: 'pointer',
                  borderBottom: i < options.length - 1 ? '1px solid #F1F5F9' : 'none',
                  '&:hover': { backgroundColor: '#F8FAFC' },
                }}
              >
                {multiple && (
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    sx={{
                      p: 0,
                      color: '#C5C5C5',
                      '& .MuiSvgIcon-root': { fontSize: 16, borderRadius: '4px' },
                      '&.Mui-checked': { color: '#007BC6' },
                      '& .MuiCheckbox-root': { border: '1px solid #C5C5C5', borderRadius: '4px' },
                    }}
                  />
                )}
                <Typography sx={{ fontSize: '13px', color: isSelected ? '#007BC6' : '#385576', fontWeight: isSelected ? 600 : 400, fontFamily: "'Open Sans', sans-serif" }}>{opt.label}</Typography>
              </Box>
            );
          })}

        </Box>
      </Popover>
    </Box>
  );
};

export default DropdownSelect;